// Sprachen der Mandanten-App, der Anmeldung und des Mitarbeiter-Links (Sam 11.10.: „englisch, türkisch, deutsch und
// arabisch … also switchen kann“). Die Texte stehen im Code auf Deutsch: t('Belege hochladen'). Die Übersetzungen
// liegen je Sprache und Bereich in en/*.json, tr/*.json, ar/*.json (deutscher Text → Übersetzung) und werden erst
// geladen, wenn jemand die Sprache wählt. Fehlt eine Übersetzung, bleibt der deutsche Text. Die Kanzlei bleibt Deutsch.
// Rechtlich verbindlich sind die deutschen Texte (Unterschrift, Kündigung, Datenschutz).

export type Lang = 'de' | 'en' | 'tr' | 'ar';
export const LANGS: { key: Lang; label: string }[] = [
  { key: 'de', label: 'Deutsch' },
  { key: 'en', label: 'English' },
  { key: 'tr', label: 'Türkçe' },
  { key: 'ar', label: 'العربية' },
];
export const isLang = (v: unknown): v is Lang => LANGS.some((l) => l.key === v);

type Dict = Record<string, string>;
type Vars = Record<string, string | number>;
type Loader = () => Promise<{ default: Dict }>;
const FILES: Record<Exclude<Lang, 'de'>, Record<string, Loader>> = {
  en: import.meta.glob<{ default: Dict }>('./en/*.json'),
  tr: import.meta.glob<{ default: Dict }>('./tr/*.json'),
  ar: import.meta.glob<{ default: Dict }>('./ar/*.json'),
};
export const loaded: Partial<Record<Lang, { dict: Dict; patterns: Pattern[] }>> = { de: { dict: {}, patterns: [] } };

export async function load(lang: Lang) {
  if (lang === 'de' || loaded[lang]) return;
  const parts = await Promise.all(Object.values(FILES[lang]).map((f) => f()));
  const dict: Dict = Object.assign({}, ...parts.map((p) => p.default));
  loaded[lang] = { dict, patterns: patternsOf(dict) };
}

// Sprache, in der t() gerade übersetzt (setzt LangProvider beim Zeichnen)
let current: Lang = 'de';
let dict: Dict = {};
let patterns: Pattern[] = [];

// Sprache für t() setzen (nur LangProvider, die Wörterbücher sind dann geladen)
export function applyLang(lang: Lang) {
  const d = loaded[lang]!;
  current = lang;
  dict = d.dict;
  patterns = d.patterns;
}

function fill(s: string, vars?: Vars): string {
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

// Übersetzt einen deutschen Text aus dem Code. Platzhalter in {…}: t('{n} Dateien hochgeladen', { n: 3 })
export function t(de: string, vars?: Vars): string {
  return fill(current === 'de' ? de : (dict[de] ?? de), vars);
}

// Merkt einen Text zum Übersetzen vor, ohne ihn schon zu übersetzen: für Listen außerhalb von Komponenten
// (const NAV = [{ label: tl('Übersicht') }]), beim Anzeigen dann t(n.label). So findet die Prüfung den Text.
export const tl = (de: string) => de;

// Übersetzt einen fertigen deutschen Text vom Server (Fehlermeldung, Glocke): genau oder nach einem Muster mit
// Platzhaltern, z. B. „{name} ist angemeldet“. Die eingesetzten Teile werden übersetzt, wenn es sie einzeln gibt.
export function tx(de: string): string {
  if (current === 'de' || !de) return de;
  const exact = dict[de];
  if (exact !== undefined) return exact;
  for (const p of patterns) {
    const m = p.re.exec(de);
    if (m) return fill(p.out, Object.fromEntries(p.names.map((n, i) => [n, tx(m[i + 1]!)])));
  }
  return de;
}

interface Pattern {
  re: RegExp;
  names: string[];
  out: string;
}
function patternsOf(d: Dict): Pattern[] {
  // Muster fast ohne festen Text („{a} · {b}“) würden fremde Texte halb übersetzen
  return Object.entries(d)
    .filter(([k]) => /\{\w+\}/.test(k) && k.replace(/\{\w+\}/g, '').trim().length >= 3)
    .map(([k, out]) => {
      const names: string[] = [];
      const src = k
        .split(/(\{\w+\})/)
        .map((part) => {
          const m = /^\{(\w+)\}$/.exec(part);
          if (!m) return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          names.push(m[1]!);
          return '(.+?)';
        })
        .join('');
      return { re: new RegExp(`^${src}$`, 's'), names, out };
    })
    .sort((a, b) => b.re.source.length - a.re.source.length);
}

export function currentLang(): Lang {
  return current;
}

